import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D7fromD1 = () => {
  const title = "Calculation of D7 (Saptamsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D7 chart, or Saptamsa chart, is used in Vedic astrology to analyze
        children, fertility, and progeny. Below is the step-by-step process to
        calculate the D7 chart from the D1 (Rasi) chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D7 (Saptamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Venus is located at 15° Gemini, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Sign into Seven Parts
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into seven equal parts, each spanning
        approximately 4.2857°. To find the Saptamsa position of a planet, we
        divide its degree in the sign by 4.2857.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Saptamsa \ Position = \frac{Degree \ in \ Sign}{4.2857}`}
        />
      </Box>

      <Typography variant="body1" paragraph>
        Depending on the result, we can determine the section in which the
        planet lies:
      </Typography>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`0 \leq \frac{Degree \ in \ Sign}{4.2857} < 1`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>first 4.2857°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`1 \leq \frac{Degree \ in \ Sign}{4.2857} < 2`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>second 4.2857°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`2 \leq \frac{Degree \ in \ Sign}{4.2857} < 3`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>third 4.2857°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`3 \leq \frac{Degree \ in \ Sign}{4.2857} < 4`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>fourth 4.2857°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`4 \leq \frac{Degree \ in \ Sign}{4.2857} < 5`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>fifth 4.2857°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`5 \leq \frac{Degree \ in \ Sign}{4.2857} < 6`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>sixth 4.2857°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`6 \leq \frac{Degree \ in \ Sign}{4.2857} < 7`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>seventh 4.2857°</strong>.
        </Typography>
      </Box>

      <Typography variant="h5" gutterBottom>
        3. Assign the Saptamsa Based on the Planet's Degree
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>First 4.2857°:</strong> The planet is assigned to the{" "}
        <strong>same sign</strong> as the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Second 4.2857°:</strong> The planet is assigned to the{" "}
        <strong>7th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third 4.2857°:</strong> The planet is assigned to the{" "}
        <strong>4th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourth 4.2857°:</strong> The planet is assigned to the{" "}
        <strong>10th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifth 4.2857°:</strong> The planet is assigned to the{" "}
        <strong>5th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Sixth 4.2857°:</strong> The planet is assigned to the{" "}
        <strong>11th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Seventh 4.2857°:</strong> The planet is assigned to the{" "}
        <strong>6th sign</strong> from the D1 sign.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Venus at 15° Gemini
      </Typography>

      <Typography variant="body1" paragraph>
        Gemini is the D1 sign. Venus at 15° falls in the fourth 4.2857° (between
        12.8571° and 17.1428°). Therefore, it belongs to the{" "}
        <strong>10th sign</strong> from Gemini, which is <strong>Pisces</strong>
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D7 (Saptamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D7
        (Saptamsa) position from the D1 (Rasi) chart, using the planet's degree
        and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_saptamsa(degree, sign):
    # List of zodiac signs in order
    zodiac_signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']

    # Get the index of the current sign in the zodiac cycle
    sign_index = zodiac_signs.index(sign)

    # Determine which Saptamsa the planet is in based on its degree
    if degree <= 4.2857:
        # First 4.2857° belongs to the same sign
        saptamsa_sign = zodiac_signs[sign_index]
    elif 4.2857 < degree <= 8.5714:
        # Second 4.2857° belongs to the 7th sign from the current sign
        saptamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 8.5714 < degree <= 12.8571:
        # Third 4.2857° belongs to the 4th sign from the current sign
        saptamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 12.8571 < degree <= 17.1428:
        # Fourth 4.2857° belongs to the 10th sign from the current sign
        saptamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 17.1428 < degree <= 21.4285:
        # Fifth 4.2857° belongs to the 5th sign from the current sign
        saptamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 21.4285 < degree <= 25.7142:
        # Sixth 4.2857° belongs to the 11th sign from the current sign
        saptamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    else:
        # Seventh 4.2857° belongs to the 6th sign from the current sign
        saptamsa_sign = zodiac_signs[(sign_index + 5) % 12]

    return saptamsa_sign

# Example usage
planet = "Venus"
degree = 15
sign = "Gemini"

saptamsa_sign = calculate_saptamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {saptamsa_sign} in the D7 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Saptamsa for a planet based on
        its degree and sign in the D1 chart. The program divides the sign into
        seven equal parts and assigns the Saptamsa based on the planet's degree.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the sign into seven parts: the first 4.2857°, the
        second 4.2857°, the third 4.2857°, and so on. It assigns the Saptamsa
        based on the planet's degree within the sign:
        <br />
        - First 4.2857° belongs to the same sign.
        <br />
        - Second 4.2857° belongs to the 7th sign from the D1 sign.
        <br />
        - Third 4.2857° belongs to the 4th sign from the D1 sign.
        <br />
        - Fourth 4.2857° belongs to the 10th sign from the D1 sign.
        <br />
        - Fifth 4.2857° belongs to the 5th sign from the D1 sign.
        <br />
        - Sixth 4.2857° belongs to the 11th sign from the D1 sign.
        <br />- Seventh 4.2857° belongs to the 6th sign from the D1 sign.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D7fromD1;
